// ---------------------------------------------------------------------------
// เลขที่เอกสารเรียงอัตโนมัติ — ต่อร้าน + ประเภทเอกสาร + ปี ค.ศ. (เขตเวลา Asia/Bangkok)
// ---------------------------------------------------------------------------
// รูปแบบ  <PREFIX>-<ปี>-<ลำดับ 6 หลัก>  เช่น TX-2026-000001 (ร้านแก้ prefix ได้ที่ organizations.docPrefixes)
//   - โหมด Google Sheets : action `seq.next` ใน Code.gs ใต้ LockService (ชีต doc_sequences) กันเลขชนเมื่อกดพร้อมกัน
//   - โหมด localStorage  : counter ในคอลเลกชัน docSequences ของร้าน
// เลขที่ออกแล้วไม่นำกลับมาใช้ซ้ำ — ขอเลขเฉพาะตอน "ออกเอกสาร" จริง (ฉบับร่างไม่กินเลข)
// ---------------------------------------------------------------------------
import { gsEnabled, gsPost } from "./gsClient";
import { table } from "../mock/store";

export const DOC_TYPES = {
  invoice: { prefix: "INV", label: "ใบแจ้งหนี้" },
  quotation: { prefix: "QT", label: "ใบเสนอราคา" },
  receipt: { prefix: "RC", label: "ใบเสร็จรับเงิน" },
  tax_invoice: { prefix: "TX", label: "ใบกำกับภาษี" },
  receipt_tax_invoice: { prefix: "RT", label: "ใบเสร็จรับเงิน/ใบกำกับภาษี" },
  credit_note: { prefix: "CN", label: "ใบลดหนี้" },
  // รหัสรายการ (ไม่ใช่เอกสารบัญชี) — ไม่แสดงในหน้าตั้งค่า prefix ของร้าน
  customer: { prefix: "C", label: "รหัสลูกค้า" },
  appointment: { prefix: "AP", label: "รหัสนัดหมาย" },
  product: { prefix: "P", label: "รหัสสินค้า" },
  pos: { prefix: "POS", label: "เลขบิล POS" },
  sales_order: { prefix: "SO", label: "เลขออเดอร์" },
};

const PREFIX_PATTERN = /^[A-Za-z0-9]{1,8}$/;

export const cleanPrefix = (value) => {
  const text = String(value ?? "").trim();
  return PREFIX_PATTERN.test(text) ? text.toUpperCase() : "";
};

/** prefix ที่ร้านตั้งไว้ (organizations.docPrefixes) ไม่งั้นใช้ค่าเริ่มต้นของประเภทนั้น */
export const resolvePrefix = (organization, docType) =>
  cleanPrefix(organization?.docPrefixes?.[docType]) || DOC_TYPES[docType]?.prefix || "DOC";

/** ปี ค.ศ. ตามเวลากรุงเทพ (เที่ยงคืนวันที่ 1 ม.ค. ตามเวลาไทย = ขึ้นปีใหม่) */
export const bangkokYear = (date = new Date()) =>
  Number(new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Bangkok", year: "numeric" }).format(date));

export const formatDocNo = (prefix, year, seq) => `${prefix}-${year}-${String(seq).padStart(6, "0")}`;

/**
 * ขอเลขถัดไป — { docNo, seq, year }
 * `now` ใช้ในเทสต์เท่านั้น (โหมด Sheets เซิร์ฟเวอร์ใช้เวลาของตัวเอง)
 */
export const nextDocNo = async ({ orgId, docType, prefix, organization, now }) => {
  if (!orgId) throw new Error("ไม่พบร้านที่จะออกเลขที่เอกสาร");
  if (!DOC_TYPES[docType]) throw new Error(`ไม่รู้จักประเภทเอกสาร: ${docType}`);
  const usePrefix = cleanPrefix(prefix) || resolvePrefix(organization, docType);

  if (gsEnabled()) {
    const result = await gsPost("seq.next", { org: orgId, docType });
    if (!result?.seq || !result?.year) throw new Error("ขอเลขที่เอกสารจาก Google Sheets ไม่สำเร็จ");
    return { docNo: formatDocNo(usePrefix, result.year, result.seq), seq: result.seq, year: result.year };
  }

  const year = bangkokYear(now || new Date());
  const rows = table(orgId, "docSequences");
  const id = `${docType}:${year}`;
  const current = rows.find(id);
  const seq = (Number(current?.last) || 0) + 1;
  if (current) rows.update(id, { last: seq });
  else rows.create({ id, docType, year, last: seq }, "seq");
  return { docNo: formatDocNo(usePrefix, year, seq), seq, year };
};
